import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { StartMenu } from './StartMenu';
import { PixelIcon } from '../PixelIcon/PixelIcon';
import { glitchAnimation, cmdChaosAnimation, taskbarChaosAnimation } from '../../styles/meltEffects';

const TaskbarItem = styled.div`
  height: 36px;
  min-width: 150px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  background: ${props => props.isActive ? '#000080' : '#c3c3c3'};
  color: ${props => props.isActive ? '#ffffff' : '#000000'};
  border: 2px solid;
  border-color: ${props => 
    props.isActive 
      ? '#404040 #ffffff #ffffff #404040' 
      : '#ffffff #404040 #404040 #ffffff'
  };
  margin-right: 4px;
  font-family: 'VT323', monospace;
  cursor: pointer;
  user-select: none;

  &:hover {
    filter: brightness(0.95);
  }
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 16px;
  height: 90%;
  min-width: 160px;
  background: #dfdfdf;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  font-family: 'VT323', monospace;
  cursor: pointer;
  box-shadow: inset 1px 1px 0px #ffffff, inset -1px -1px 0px #808080;
  position: relative;
  
  &:active {
    border-color: #808080 #ffffff #ffffff #808080;
    box-shadow: inset 1px 1px 0px #808080, inset -1px -1px 0px #ffffff;
    padding-top: 3px;
    background: #d0d0d0;
    img {
      transform: translateY(1px);
    }
    span {
      transform: translateY(1px);
    }
  }
  
  &:hover {
    background: #efefef;
  }
  
  img {
    width: 28px;
    height: 28px;
  }

  span {
    font-size: 22px;
    font-weight: normal;
    text-shadow: 1px 1px #ffffff;
  }

  /* Add responsive text handling */
  @media (max-width: 768px) {
    padding: 2px 8px;
    gap: 4px;
    min-width: 100px;
    
    img {
      width: 18px;
      height: 18px;
    }
    
    span {
      font-size: 12px;
    }
  }

  /* For very small screens */
  @media (max-width: 360px) {
    min-width: auto;
    padding: 2px 6px;
    
    span {
      display: none;
    }
    
    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const TaskbarDivider = styled.div`
  width: 2px;
  height: 85%;
  background: #808080;
  margin: 0 4px;
`;

const SystemTray = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  height: 100%;
  border-left: 1px solid #888;
`;

const Clock = styled.div`
  margin-left: auto;
  padding: 0 10px;
  font-family: 'VT323', monospace;
  border-left: 2px solid #808080;
  height: 100%;
  display: flex;
  align-items: center;
`;

const getWindowIcon = (name) => {
  switch(name) {
    case 'Notepad':
      return '📝';
    case 'cmd':
      return '💻';
    case 'Recycle Bin':
      return '🗑️';
    default:
      return '📄';
  }
};

const getWindowTitle = (name) => {
  switch(name) {
    case 'cmd':
      return 'Command Prompt';
    default:
      return name;
  }
};

const TaskbarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background-color: #c0c0c0;
  border-top: 2px solid #ffffff;
  display: flex;
  align-items: center;
  padding: 0 2px;
  z-index: 1000;

  @media (max-width: 768px) {
    height: 50px;
    padding: 0 5px;
  }

  &.melting {
    animation: ${taskbarChaosAnimation} 0.3s ease-in-out infinite;
    
    ${TaskbarItem} {
      animation: ${glitchAnimation} 0.2s steps(2) infinite;
      animation-delay: ${props => Math.random() * 0.5}s;
    }

    ${StartButton} {
      animation: ${cmdChaosAnimation} 0.25s ease-in-out infinite;
      filter: hue-rotate(${props => Math.random() * 360}deg);
    }
  }
`;

export const Taskbar = ({ 
  openWindows = [], 
  activeWindow, 
  onWindowClick, 
  showStartMenu,
  onStartClick,
  onStartMenuItemClick,
  isMelting = false
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <TaskbarContainer className={`taskbar ${isMelting ? 'melting' : ''}`}>
      <StartButton onClick={onStartClick}>
        <img src="/images/dOSlogo.svg" alt="Start" />
        <b><span>dOS menu</span></b>
      </StartButton>
      
      {showStartMenu && (
        <StartMenu 
          onClose={() => onStartClick(false)}
          onItemClick={onStartMenuItemClick}
        />
      )}

      <TaskbarDivider />
      
      {openWindows.map(window => (
        <TaskbarItem
          key={window.id}
          isActive={activeWindow === window.id}
          onClick={() => onWindowClick(window.id)}
        >
          {getWindowIcon(window.name)} {getWindowTitle(window.name)}
        </TaskbarItem>
      ))}

      <Clock>
        {formatTime(currentTime)}
      </Clock>
    </TaskbarContainer>
  );
};